import { c as _c } from "react/compiler-runtime";
import { Button } from '@sprd/sprd-component-kit/v2';
import React, { Suspense } from 'react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { CartAndLineItemGroups, CommerceToolsCart, SprdVoucherData } from '@/types/commercetools';
import { useIsExpressCheckoutEnabled } from '@/hooks/useIsExpressCheckoutEnabled';
import { useCentPriceFormatter } from '@/hooks/usePriceFormatters';
import ExpressCheckoutSectionSkeleton from '@/components/ExpressCheckoutSection/ExpressCheckoutSectionSkeleton';
import { getCheckoutUrl } from '@/utility/getCheckoutUrl';
import { Locale } from '@/utility/locale';
import { FreeShippingBanner } from './FreeShippingBanner';
const ExpressCheckoutSectionMiniBasket = dynamic(() => import('@/components/ExpressCheckoutSection/ExpressCheckoutSectionMiniBasket'));
function getTotalDirectDiscount(cart: CommerceToolsCart) {
  const lineItemDiscounts = [...cart.lineItems, ...cart.customLineItems].map(lineItem => lineItem.discountedPricePerQuantity.length ? lineItem.discountedPricePerQuantity.reduce((result, discount) => result + discount.quantity * discount.discountedPrice.includedDiscounts.reduce((discountSum, includedDiscount) => discountSum + includedDiscount.discountedAmount.centAmount, 0), 0) : 0);
  return lineItemDiscounts.reduce((result, discount) => result + discount, 0);
}
function getShippingDiscount(cart: CommerceToolsCart) {
  const discountedPrice = cart.shippingInfo?.discountedPrice;
  if (!discountedPrice) {
    return 0;
  }
  return discountedPrice.includedDiscounts.reduce((result, discount) => result + discount.discountedAmount.centAmount, 0);
}
function getVolumeDiscount(cart: CommerceToolsCart) {
  return [...cart.lineItems, ...cart.customLineItems].map(item => (item?.custom?.fields?.sprdDiscountAmount?.centAmount || 0) * item.quantity).reduce((result, cur) => result + cur, 0) || 0;
}
function getMoneyTypeDiscount(cart: CommerceToolsCart) {
  if (!cart.custom.fields.sprdVoucherJson) {
    return 0;
  }
  const voucherJson = JSON.parse(cart.custom.fields.sprdVoucherJson) as SprdVoucherData;
  return voucherJson.redeemableMoneyTypeCoupons.reduce((result, discount) => result + discount.appliedAmount.centAmount, 0);
}
function calculateBasketSubtotal(cart: CommerceToolsCart, {
  directDiscount,
  shippingDiscount,
  volumeDiscount
}: ReturnType<typeof getBasketDiscounts>) {
  return cart.totalPrice.centAmount - (cart.shippingInfo?.price.centAmount ?? 0) + volumeDiscount + directDiscount + shippingDiscount;
}
function getBasketDiscounts(cart: CommerceToolsCart) {
  return {
    directDiscount: getTotalDirectDiscount(cart),
    shippingDiscount: getShippingDiscount(cart),
    volumeDiscount: getVolumeDiscount(cart)
  };
}
export function getBasketSubtotal(cart: CommerceToolsCart) {
  return calculateBasketSubtotal(cart, getBasketDiscounts(cart));
}
const PriceItems = t0 => {
  const $ = _c(58);
  const {
    cart,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t = useTranslations();
  const formatPrice = useCentPriceFormatter(cart.totalPrice.currencyCode);
  let moneyTypeDiscount;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== cart || $[1] !== formatPrice || $[2] !== t) {
    const {
      directDiscount,
      shippingDiscount,
      volumeDiscount
    } = getBasketDiscounts(cart);
    moneyTypeDiscount = getMoneyTypeDiscount(cart);
    const subTotal = calculateBasketSubtotal(cart, {
      directDiscount,
      shippingDiscount,
      volumeDiscount
    });
    let t9;
    if ($[11] !== t) {
      t9 = t("miniBasket.subtotal");
      $[11] = t;
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    const t10 = formatPrice(subTotal);
    if ($[13] !== t10 || $[14] !== t9) {
      t5 = {
        key: "subtotal",
        label: t9,
        value: t10,
        isShown: true
      };
      $[13] = t10;
      $[14] = t9;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t11;
    if ($[16] !== t) {
      t11 = t("miniBasket.discount");
      $[16] = t;
      $[17] = t11;
    } else {
      t11 = $[17];
    }
    let t12;
    if ($[18] !== formatPrice || $[19] !== volumeDiscount) {
      t12 = formatPrice(volumeDiscount);
      $[18] = formatPrice;
      $[19] = volumeDiscount;
      $[20] = t12;
    } else {
      t12 = $[20];
    }
    const t13 = volumeDiscount > 0;
    if ($[21] !== t11 || $[22] !== t12 || $[23] !== t13) {
      t6 = {
        key: "volume-discount",
        label: t11,
        value: t12,
        isShown: t13
      };
      $[21] = t11;
      $[22] = t12;
      $[23] = t13;
      $[24] = t6;
    } else {
      t6 = $[24];
    }
    let t14;
    if ($[25] !== t) {
      t14 = t("miniBasket.couponDiscount");
      $[25] = t;
      $[26] = t14;
    } else {
      t14 = $[26];
    }
    let t15;
    if ($[27] !== directDiscount || $[28] !== formatPrice) {
      t15 = formatPrice(directDiscount);
      $[27] = directDiscount;
      $[28] = formatPrice;
      $[29] = t15;
    } else {
      t15 = $[29];
    }
    const t16 = directDiscount > 0;
    if ($[30] !== t14 || $[31] !== t15 || $[32] !== t16) {
      t7 = {
        key: "coupon-discount",
        label: t14,
        value: t15,
        isShown: t16
      };
      $[30] = t14;
      $[31] = t15;
      $[32] = t16;
      $[33] = t7;
    } else {
      t7 = $[33];
    }
    let t17;
    if ($[34] !== t) {
      t17 = t("miniBasket.shippingCost");
      $[34] = t;
      $[35] = t17;
    } else {
      t17 = $[35];
    }
    const t18 = cart.shippingInfo?.price.centAmount ?? 0;
    let t19;
    if ($[36] !== formatPrice || $[37] !== t18) {
      t19 = formatPrice(t18);
      $[36] = formatPrice;
      $[37] = t18;
      $[38] = t19;
    } else {
      t19 = $[38];
    }
    if ($[39] !== t17 || $[40] !== t19) {
      t8 = {
        key: "shipping-costs",
        label: t17,
        value: t19,
        isShown: true
      };
      $[39] = t17;
      $[40] = t19;
      $[41] = t8;
    } else {
      t8 = $[41];
    }
    t2 = "money-type-coupon";
    if ($[42] !== t) {
      t3 = t("miniBasket.voucher");
      $[42] = t;
      $[43] = t3;
    } else {
      t3 = $[43];
    }
    t4 = formatPrice(moneyTypeDiscount);
    $[0] = cart;
    $[1] = formatPrice;
    $[2] = t;
    $[3] = moneyTypeDiscount;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    moneyTypeDiscount = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
  }
  const t9 = moneyTypeDiscount > 0;
  let t10;
  if ($[44] !== t2 || $[45] !== t3 || $[46] !== t4 || $[47] !== t9) {
    t10 = {
      key: t2,
      label: t3,
      value: t4,
      isShown: t9
    };
    $[44] = t2;
    $[45] = t3;
    $[46] = t4;
    $[47] = t9;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  let t11;
  if ($[49] !== t10 || $[50] !== t5 || $[51] !== t6 || $[52] !== t7 || $[53] !== t8) {
    t11 = [t5, t6, t7, t8, t10];
    $[49] = t10;
    $[50] = t5;
    $[51] = t6;
    $[52] = t7;
    $[53] = t8;
    $[54] = t11;
  } else {
    t11 = $[54];
  }
  const prices = t11;
  let t12;
  if ($[55] !== className || $[56] !== prices) {
    t12 = prices.map((price, i) => {
      if (!price.isShown) {
        return null;
      }
      const isDiscount = ["volume-discount", "coupon-discount", "money-type-coupon"].includes(price.key);
      return <PriceItem key={`${price.key}-${i}`} price={price} isDiscount={isDiscount} className={className} />;
    });
    $[55] = className;
    $[56] = prices;
    $[57] = t12;
  } else {
    t12 = $[57];
  }
  return t12;
};
const PriceItem = t0 => {
  const $ = _c(16);
  const {
    price,
    isDiscount,
    className
  } = t0;
  const t1 = price.key;
  const t2 = price.key === "total" ? "pt-2 font-bold" : "text-sm";
  let t3;
  if ($[0] !== className || $[1] !== isDiscount || $[2] !== t2) {
    t3 = clsx("flex items-center justify-between", className, t2, {
      "text-red-600": isDiscount
    });
    $[0] = className;
    $[1] = isDiscount;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== price.label) {
    t4 = <span>{price.label}</span>;
    $[4] = price.label;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isDiscount) {
    t5 = clsx({
      "text-red-600": isDiscount
    });
    $[6] = isDiscount;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = isDiscount ? "-" + price.value : price.value;
  let t7;
  if ($[8] !== t5 || $[9] !== t6) {
    t7 = <span className={t5}>{t6}</span>;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== price.key || $[12] !== t3 || $[13] !== t4 || $[14] !== t7) {
    t8 = <li key={t1} className={t3}>{t4}{t7}</li>;
    $[11] = price.key;
    $[12] = t3;
    $[13] = t4;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
};
export const DesktopBasketPrices = t0 => {
  const $ = _c(32);
  const {
    cartAndLineItemGroups,
    locale,
    freeShippingBannerRemainderCents
  } = t0;
  const t = useTranslations("miniBasket");
  const isExpressCheckoutEnabled = useIsExpressCheckoutEnabled();
  const cart = cartAndLineItemGroups.cart;
  const formatPrice = useCentPriceFormatter(cart.totalPrice.currencyCode);
  const moneyTypeDiscount = getMoneyTypeDiscount(cart);
  const total = cart.totalPrice.centAmount - moneyTypeDiscount;
  let t1;
  if ($[0] !== cartAndLineItemGroups.cart) {
    t1 = <PriceItems cart={cartAndLineItemGroups.cart} className="hidden sm:flex" />;
    $[0] = cartAndLineItemGroups.cart;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== cart.totalPrice.currencyCode || $[3] !== freeShippingBannerRemainderCents) {
    t2 = freeShippingBannerRemainderCents !== undefined && <FreeShippingBanner remainderCentAmount={freeShippingBannerRemainderCents} currencyCode={cart.totalPrice.currencyCode} />;
    $[2] = cart.totalPrice.currencyCode;
    $[3] = freeShippingBannerRemainderCents;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("total");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== formatPrice || $[8] !== total) {
    t4 = formatPrice(total);
    $[7] = formatPrice;
    $[8] = total;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <PriceItem price={{
      key: "total",
      label: t3,
      value: t4,
      isShown: true
    }} isDiscount={false} />;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== cart.totalPrice.currencyCode || $[14] !== cartAndLineItemGroups || $[15] !== isExpressCheckoutEnabled || $[16] !== locale) {
    t6 = isExpressCheckoutEnabled && <Suspense fallback={<ExpressCheckoutSectionSkeleton />}><ExpressCheckoutSectionMiniBasket cartAndLineItemGroups={cartAndLineItemGroups} currencyCode={cart.totalPrice.currencyCode} locale={locale} /></Suspense>;
    $[13] = cart.totalPrice.currencyCode;
    $[14] = cartAndLineItemGroups;
    $[15] = isExpressCheckoutEnabled;
    $[16] = locale;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== cartAndLineItemGroups.cart.id || $[19] !== locale) {
    t7 = getCheckoutUrl(cartAndLineItemGroups.cart.id, locale);
    $[18] = cartAndLineItemGroups.cart.id;
    $[19] = locale;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t) {
    t8 = t("checkout");
    $[21] = t;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t7 || $[24] !== t8) {
    t9 = <li><Button variant="primary-cta" size="l" className="w-full" asChild={true}><Link href={t7}>{t8}</Link></Button></li>;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t1 || $[27] !== t2 || $[28] !== t5 || $[29] !== t6 || $[30] !== t9) {
    t10 = <ul className="flex w-full flex-col justify-end gap-1 border-t border-t-neutral-200 p-4 sm:px-8 sm:py-6 md:gap-2">{t1}{t2}{t5}{t6}{t9}</ul>;
    $[26] = t1;
    $[27] = t2;
    $[28] = t5;
    $[29] = t6;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
};
export const MobileBasketPrices = t0 => {
  const $ = _c(2);
  const {
    cart
  } = t0;
  let t1;
  if ($[0] !== cart) {
    t1 = <ul className="flex w-full flex-col justify-end gap-2.5 border-t border-t-neutral-200 py-4 sm:hidden"><PriceItems cart={cart} /></ul>;
    $[0] = cart;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};